import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, CloudUpload } from 'lucide-react';
import { formatWeight } from '@vj/shared';
import { api } from '../../lib/api.js';
import { useListQuery } from '../../hooks/useListQuery.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';

const STATUS_TONE = {
  draft: 'text-warning',
  received: 'text-muted',
  in_progress: 'text-warning',
  ready: 'text-primary',
  delivered: 'text-success',
  cancelled: 'text-danger',
};

const STATUS_LABEL = {
  draft: 'Draft',
  received: 'Received',
  in_progress: 'In progress',
  ready: 'Ready',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const COLUMNS = [
  { key: 'voucher_no', header: 'Voucher #', sortKey: 'voucher_no', render: (r) => r.voucher_no ?? <span className="italic text-muted">Draft</span> },
  { key: 'voucher_date', header: 'Date', sortKey: 'voucher_date', render: (r) => new Date(r.voucher_date).toLocaleDateString('en-IN') },
  { key: 'customer_name', header: 'Customer' },
  { key: 'karigar_name', header: 'Aasari', render: (r) => r.karigar_name || '—' },
  {
    key: 'expected_delivery_date',
    header: 'Expected',
    render: (r) => {
      if (!r.expected_delivery_date) return '—';
      const overdue = !['delivered', 'cancelled'].includes(r.status) && new Date(r.expected_delivery_date) < new Date(new Date().toDateString());
      return <span className={overdue ? 'text-danger' : ''}>{new Date(r.expected_delivery_date).toLocaleDateString('en-IN')}</span>;
    },
  },
  { key: 'total_weight', header: 'Weight', align: 'right', render: (r) => formatWeight(r.total_weight) },
  { key: 'status', header: 'Status', render: (r) => <span className={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</span> },
];

export default function RepairsListPage() {
  const [query, onQueryChange] = useListQuery({ sort: 'voucher_date' });
  const debouncedSearch = useDebouncedValue(query.search, 350);
  const navigate = useNavigate();
  const location = useLocation();

  const listQuery = useQuery({
    queryKey: ['repairs', { ...query, search: debouncedSearch }],
    queryFn: () => api.get('/repairs', { query: { ...query, search: debouncedSearch } }),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Repair vouchers</h1>
        <Button onClick={() => navigate('/repairs/new')}>
          <Plus size={17} aria-hidden="true" /> New voucher
        </Button>
      </div>

      {location.state?.savedOffline ? (
        <p className="flex items-center gap-2 rounded-card border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          <CloudUpload size={16} aria-hidden="true" /> Saved on this device — it&rsquo;ll upload automatically once you&rsquo;re back online.
        </p>
      ) : null}

      <DataTable
        columns={COLUMNS}
        data={listQuery.data?.data ?? []}
        meta={listQuery.data?.meta}
        query={query}
        onQueryChange={onQueryChange}
        isLoading={listQuery.isLoading}
        onRowClick={(row) => navigate(`/repairs/${row.id}`)}
        searchPlaceholder="Search by voucher number, customer or mobile…"
        emptyLabel="No repair vouchers yet."
      />
    </div>
  );
}
